import { HTMLAttributes, useEffect, useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import { Button } from 'antd';
import styled from '@emotion/styled';
import dayjs from 'lib/dayjs';
import { useAddVoucherToMap, useVoucherMapValue } from 'hooks/business/conversation';
import { useSetVoucherDetailShow, useSetVoucherDetailData } from 'hooks/business/voucherDetail';
import { voucher as fetchVoucher } from 'services/api/conversation';
import voucherBackgroundImage from './assets/voucherBkLazada.png';
import { useChatConversationContext } from 'views/Conversation/Chat/contexts/ChatConversationContext';

const Container = styled.section`
  display: flex;
  flex-direction: row;
  width: 420px;
  height: 110px;
  border-radius: 8px;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  background-image: url(${`"${voucherBackgroundImage}"`});
  color: #fe4f65;
`;

const VoucherValue = styled.div`
  width: 172px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const MinConsumeWrap = styled.div`
  text-align: center;
`;

const MinConsume = styled.span`
  margin-right: 2px;
  word-break: break-all;
`;

const VoucherTypeWrap = styled.div`
  width: 244px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  button {
    float: right;
    margin-right: 8px;
    min-width: 60px;
  }
`;

const VoucherTypeCnt = styled.div`
  margin: 0 0 0 23px;
  > div > span {
    font-size: 16px;
    margin-bottom: 2px;
    display: block;
  }
  footer {
    font-size: 12px;
    margin-bottom: 4px;
  }
`;

const DiscountAmountWrap = styled.span`
  font-size: 16px;
`;

const DiscountAmount = styled.span`
  font-size: 22px;
  margin-right: 2px;
`;

export interface VoucherMessageLazadaProps extends HTMLAttributes<HTMLDivElement> {
  voucherId: string;
  voucherCode: string;
}

function VoucherMessageLazada(props: VoucherMessageLazadaProps) {
  const { voucherId, voucherCode, ...otherProps } = props;

  const { t } = useTranslate();

  const voucherMap = useVoucherMapValue();

  const addVoucherToMap = useAddVoucherToMap();

  const { currentConversation: conversation } = useChatConversationContext();

  const setVoucherDetailShow = useSetVoucherDetailShow();

  const setVoucherDetailData = useSetVoucherDetailData();

  const { [Number(voucherId).toString()]: voucher } = voucherMap;

  const refsCurrent = {
    addVoucherToMap,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  const { channel, storeId } = conversation || {};

  useEffect(() => {
    if (voucher || !channel || !storeId) {
      return;
    }

    fetchVoucher({
      internal_store_id: storeId,
      external_voucher_id: voucherId,
      platform: channel,
    })
      .then((res) => {
        if (!res) {
          throw new Error('Not found');
        }

        const {
          current: { addVoucherToMap },
        } = refs;

        addVoucherToMap(res, storeId);
      })
      .catch(() => {});
  }, [voucher, voucherId, channel, storeId, addVoucherToMap]);

  if (voucher === undefined) {
    return <Container {...otherProps} />;
  }

  const {
    voucherType,
    // voucherName,
    rewardType,
    unit,
    discountAmount,
    percentage,
    startTime,
    endTime,
    minBasketPrice,
  } = voucher;

  let DiscountValue = null;
  if (rewardType === 1) {
    DiscountValue =
      discountAmount || discountAmount === 0 ? (
        <DiscountAmountWrap>
          <DiscountAmount>{discountAmount}</DiscountAmount>
          {unit}
        </DiscountAmountWrap>
      ) : null;
  } else {
    DiscountValue = percentage ? (
      <DiscountAmountWrap>
        <DiscountAmount>{percentage}</DiscountAmount>%
      </DiscountAmountWrap>
    ) : null;
  }

  return (
    <Container {...otherProps}>
      <VoucherValue>
        {DiscountValue}
        {minBasketPrice ? (
          <MinConsumeWrap>
            <MinConsume>{t('lazadaVoucher.最低消费')}</MinConsume>
            <span>
              {minBasketPrice}
              {unit}
            </span>
          </MinConsumeWrap>
        ) : null}
      </VoucherValue>
      <VoucherTypeWrap>
        <VoucherTypeCnt>
          <div>
            <span>
              {t(`voucherType.${voucherType}`)}-{t(`RewardType.${rewardType === 1 ? 1 : 2}`)}
            </span>
            <footer>
              {`${dayjs(Number(startTime) * 1000).format('YYYY/MM/DD')} - ${dayjs(
                Number(endTime) * 1000,
              ).format('YYYY/MM/DD')}`}
            </footer>
            <Button
              size="small"
              type="primary"
              onClick={() => {
                setVoucherDetailData(voucher);
                setVoucherDetailShow(true);
              }}
            >
              {t('aide.查看')}
            </Button>
          </div>
        </VoucherTypeCnt>
      </VoucherTypeWrap>
    </Container>
  );
}

export default VoucherMessageLazada;
